import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchB2cDisabled,
  selectID,
  updateUser,
} from "../features/counter/counterSlice";
import { useBooleanFlagValue } from "@openfeature/react-sdk";
import { tgoa } from "../features/search/util";
import { userLogged } from "../utils";
import { useAccount, useMsal } from "@azure/msal-react";
import flags from "../features/featureFlags/flags";

export const TYPE_USER = "user";
export const TYPE_INSTITUTION = "institution";

const getEmail = (authID) => {
  if(!authID || !authID.u) return null;
  return authID.u?.["signInNames.emailAddress"] || authID.u?.email;
};

const parsedInstituteForUser = (authID) => {
  let parsedInstition;
  if (authID.u.institution !== undefined) {
    parsedInstition = authID.u.institution;
  } else {
    const email = getEmail(authID);
    const tgoaEmail = "member@" + email?.split("@")[1];
    parsedInstition = tgoa[tgoaEmail];
  }
  return parsedInstition || authID.u.i || null;
};

const authDetails = (authId) => {
  if (!authId) return null;

  const { i, u } = authId;

  if (i && i !== " ") {
    return {
      type: TYPE_INSTITUTION,
      institution: i.name,
      institutionName: i.name,
    };
  }

  if (u && u !== " ") {
    const parsedInstitute = parsedInstituteForUser(authId);

    return {
      type: TYPE_USER,
      email: getEmail(authId),
      institution: u.i || parsedInstitute || null,
      institutionName: parsedInstitute,
      givenName: u.given_name,
      familyName: u.family_name || null,
    };
  }

  return null;
};

/**
 * @typedef {Object} InstitutionUser
 * @property {string} name - Name of the institution user
 *
 * @typedef {Object} signInNames
 * @property {string} emailAddress - Email address used for sign-in
 *
 *
 * @typedef {Object} RegularUser
 * @property {string} email - Email address of the user
 * @property {string} given_name - Given name of the user
 * @property {string} i - Institution name associated with the user
 * @property {string} [signInNames] - Email address used for sign-in (optional)
 * @property {string} [family_name] - Family name of the user (optional)
 *
 * @typedef {Object} AuthID
 * @property {InstitutionUser} [i] - Institution user (optional)
 * @property {RegularUser} [u] - Regular user (optional)
 * @property {number} [exp] - Expiration timestamp (optional)

 * Example structure of `authID`:
 * {
 *   i?: { // Institution user (optional)
 *     name: string
 *   },
 *   u?: { // Regular user (optional)
 *     email: string,
 *     i: string,
 *     given_name: string,
 *     family_name?: string,
 *     signInNames {
 *         emailAddress: string // Email address used for sign-in
 *     },
 *     exp?: number // Expiration timestamp (optional)
 *   },
 * }
 */
const useAuth = () => {
  const dispatch = useDispatch();
  const authID = useSelector(selectID);
  const b2cDisabled = useBooleanFlagValue(flags.B2C_DISABLE_AUTH, false);
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [hasFetched, setHasFetched] = useState(false);

  // Sync authID with MSAL
  useEffect(() => {
    if (!account || !account.idTokenClaims) {
      b2cDisabled
        ? authID?.u !== " " && dispatch(updateUser(" "))
        : authID?.u !== null && dispatch(updateUser(null));
      return;
    }
  }, [account, authID, b2cDisabled, dispatch]);

  useEffect(() => {
    if (!hasFetched) {
      dispatch(fetchB2cDisabled({ b2cDisabled, authID }));
      setHasFetched(true);
    }
  }, [authID, b2cDisabled, dispatch, hasFetched]);

  const isLogged = !!userLogged(authID);

  return { isLogged, authID, authDetails: authDetails(authID) };
};
export default useAuth;
